// pasteLinkPlugin.js
// Automatically turns pasted plain-text URLs into link marks.
// Aligned with prosemirror-paste-link patterns while preserving the
// custom link_embed routing for block-level URL pastes.

import {Plugin} from "prosemirror-state";
import {normalizeUrl} from "../helpers/link";

function getLinkEmbedAttrs(url) {
    let siteName = "link";
    try {
        siteName = new URL(url).hostname;
    } catch (_err) {
        // Keep fallback label when URL parsing fails.
    }

    return {
        url,
        title: url,
        description: url,
        siteName,
    };
}

export function pasteLinkPlugin() {
    return new Plugin({
        props: {
            handlePaste(view, event) {
                const rawText = event?.clipboardData?.getData("text/plain");
                // Accept single-line pastes or the first line of a multiline paste
                const firstLine = rawText?.split(/\r?\n/)[0]?.trim() ?? "";
                const url = normalizeUrl(firstLine);
                if (!url) return false;

                // Only intercept if the entire paste content was just the URL
                // (or a single line); skip if the user pasted a paragraph that
                // happens to start with a URL.
                if (rawText && rawText.trim() !== firstLine) return false;

                const {state, dispatch} = view;
                const linkMark = state.schema.marks.link;
                if (!linkMark) return false;

                const {from, to, empty} = state.selection;
                const tr = state.tr;

                if (empty) {
                    const {$from} = state.selection;
                    const isEmptyParagraph =
                        $from.parent?.type?.name === "paragraph" &&
                        $from.parent.content.size === 0;
                    const linkEmbedType = state.schema.nodes.link_embed;

                    // Paste URL into an empty paragraph → convert to a link_embed block
                    if (isEmptyParagraph && linkEmbedType) {
                        const blockStart = $from.before($from.depth);
                        const blockEnd = $from.after($from.depth);
                        tr.replaceWith(blockStart, blockEnd, linkEmbedType.create(getLinkEmbedAttrs(url)));
                        dispatch(tr.scrollIntoView());
                        return true;
                    }

                    // No selection — insert URL text marked as a link
                    tr.insertText(url, from, to);
                    tr.addMark(from, from + url.length, linkMark.create({href: url}));
                } else {
                    // Selection active — wrap selected text with the link mark
                    // (aligned with prosemirror-paste-link pattern)
                    tr.addMark(from, to, linkMark.create({href: url}));
                }

                dispatch(tr.scrollIntoView());
                return true;
            },
        },
    });
}
